// Hooks
import { useProfile } from 'modules/profile/hooks'
import { useSelector } from 'react-redux'

// Types
import type { State, Profile } from 'types'

// Utils
import { CURRENCY, currencyCode } from '@constants'

export const getDisplayAmount = (vatRate: number, price: number | string, includesVat?: boolean, showVat?: boolean): string => {
  // Price data enters from html as string
  const priceAsNumber = +price
  let amount = 0
  if (showVat) {
    amount = includesVat ? priceAsNumber : priceAsNumber * (1 + vatRate / 100)
  } else {
    amount = includesVat ? priceAsNumber * (1 - vatRate / (100 + vatRate)) : priceAsNumber
  }

  return amount.toFixed(2).toString().replace(/\.00$/, '')
}

type PriceProps = {
  price: number
  discountedPrice?: number
  includesVat?: boolean
  showVat?: boolean
  vatRate?: number
  currency: string
}

export const Price = (props: PriceProps): JSX.Element => {
  const { price, discountedPrice, includesVat, showVat, vatRate = 0, currency } = props
  const displayAmountString = getDisplayAmount(vatRate, price, includesVat, showVat)
  if (discountedPrice !== undefined && discountedPrice !== price) {
    const displayAmountAfterDiscountString = getDisplayAmount(vatRate, discountedPrice, includesVat, showVat)
    return (
      <span>
        <span
          style={{
            textDecoration: 'line-through',
            paddingRight: 5,
            opacity: 0.7,
          }}
        >
          {currencyCode(currency)}
          {displayAmountString}
        </span>
        {discountedPrice === 0 ? (
          <span>FREE</span>
        ) : (
          <span>
            {currencyCode(currency)}
            {displayAmountAfterDiscountString}
          </span>
        )}
      </span>
    )
  }
  return (
    <span>
      {currencyCode(currency)}
      {displayAmountString}
    </span>
  )
}

export const priceString = (amount: number, currency: string) => `${currencyCode(currency)}${(amount || 0).toFixed(2)}`

export const priceFormatWithVAT = (profile: Profile, price: number, price_inc_vat?: number) => {
  const showInclVAT = price_inc_vat && price_inc_vat !== price && profile.show_prices_with_vat
  return `${priceString(showInclVAT ? (price_inc_vat as number) : price, profile.currency)}${showInclVAT ? ' incl. VAT' : ''}`
}

type LicensePriceOwnProps = {
  category?: number
}

export const LicensePrice = ({ category = 1 }: LicensePriceOwnProps): JSX.Element => {
  const profile = useProfile()
  const isLoggedIn = useSelector<State, boolean>(s => s.auth.isAuthenticated)
  const licenseVatRate = isLoggedIn ? +profile.license_vat : 0
  const currency = isLoggedIn ? profile.currency || CURRENCY : CURRENCY
  const showVat = isLoggedIn ? profile.show_prices_with_vat : false
  const price = [50.0, 500.0, 1500.0][category - 1]
  return <Price showVat={showVat} vatRate={licenseVatRate} currency={currency} price={price} includesVat={false} />
}
